<template>
  <div>
    <b-overlay :show="loading.page" rounded="sm">
      <!-- Collapse one side / show both -->
      <div class="tad-seg">
        <button type="button" class="tad-seg__btn tad-seg__btn--open"
                :class="{ 'is-active': viewMode === 'open' }" @click="viewMode = 'open'">
          <i class="fa-solid fa-bolt"></i>
          <span>{{ $t('trade.openTrades') }}</span>
        </button>
        <button type="button" class="tad-seg__btn tad-seg__btn--both"
                :class="{ 'is-active': viewMode === 'both' }" @click="viewMode = 'both'">
          <i class="fa-solid fa-table-columns"></i>
          <span>{{ $t('trade.bothTrades') }}</span>
        </button>
        <button type="button" class="tad-seg__btn tad-seg__btn--closed"
                :class="{ 'is-active': viewMode === 'closed' }" @click="viewMode = 'closed'">
          <i class="fa-solid fa-clock-rotate-left"></i>
          <span>{{ $t('trade.closedTrades') }}</span>
        </button>
      </div>

      <div class="tad-split" :class="{ 'tad-split--single': viewMode !== 'both' }">
        <!-- Open trades -->
        <div class="tad-col" v-show="viewMode !== 'closed'">
          <div class="tad-col__head tad-col__head--open">
            <span><i class="fa-solid fa-bolt mr-50"></i>{{ $t('trade.openTrades') }}</span>
            <span class="tad-col__count">{{ openTrades.length }}</span>
          </div>
          <trade-details-tab v-bind:account="account" v-bind:trades="openTrades" ref="openTradesTab"
                             @newTradeRequest="newTradeRequest"
                             @editTradeRequest="editTradeRequest"
                             @closeTradeRequest="closeTradeRequest"
                             @deleteTradeRequest="deleteTradeRequest"
                             @bulkDeleteRequest="bulkDeleteRequest"
          />
        </div>

        <!-- Closed trades -->
        <div class="tad-col" v-show="viewMode !== 'open'">
          <div class="tad-col__head tad-col__head--closed">
            <span><i class="fa-solid fa-clock-rotate-left mr-50"></i>{{ $t('trade.closedTrades') }}</span>
            <span class="tad-col__count">{{ history.pagination.total || history.data.length }}</span>
          </div>
          <trade-details-tab v-bind:account="account" v-bind:trades="history.data" ref="closedTradesTab"
                             :is-history="true"
                             :summary-override="history.summary"
                             @editTradeRequest="editTradeRequest"
                             @deleteTradeRequest="deleteTradeRequest"
          />
          <!--Pagination-->
          <b-pagination
              v-if="history.pagination.total > history.pagination.size"
              v-model="history.pagination.page"
              :total-rows="history.pagination.total"
              :per-page="history.pagination.size"
              align="center"
              size="sm"
              class="my-2"
              @page-click="onHistoryPageClick"
          ></b-pagination>
        </div>
      </div>
    </b-overlay>
    <trade-order-modal ref="tradeOrderModal"  @order-placed="refresh"  @order-reopened="refreshHistory" @close="onOrderModalClose"></trade-order-modal>
    <trade-bulk-delete-modal ref="bulkDeleteModal" @done="onBulkDeleteDone"/>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { toObject } from '@/model/Utils'
import { recalculateAllProfits } from '@/model/TradingCalculation'
import TradeDetailsTab from '@/components/partial/accounts/TradeDetailsTab'
import mixinNotifications from '@/components/mixin/mixinNotifications'
import mixinBase from '@/components/mixin/mixinBase'
import TradeOrderModal from '@/components/modal/tradeOrderModal.vue'
import TradeBulkDeleteModal from '@/components/modal/TradeBulkDeleteModal.vue'

export default {
  name: 'TradeAccountDetails',
  mixins: [mixinBase, mixinNotifications],
  components: { TradeOrderModal, TradeDetailsTab, TradeBulkDeleteModal},
  data: () => ({
    loading: { page: true},
    error: null,
    accountLogin: null,
    symbols:[],
    tickers: {},
    openTrades: [],
    history: {
      data: [],
      pagination: { page: 1, pages: 0, total: 0, size: 100 },
      // Sort by openTime like the report. A null field made the core fall back
      // to sorting by the order id AS A STRING — when the order sequence
      // crossed a digit boundary (99999 → 100000), every newer trade sorted
      // BELOW the old ones and looked missing from page 1.
      sort: { direction: 'DESC', field: 'openTime' },
      // Account-wide totals for the KPI strip — the page rows alone undercount.
      summary: null,
    },
    intervalId: null,
    activeTab: 0,
    viewMode: 'both', // 'open' | 'closed' | 'both'
  }),
  props: {
    value: Object,
  },
  watch: {
    value: {
      handler() {
        if (this.accountLogin !== this.account.id) {
          this.loading.page = true;
          this.subscribeToWs(this.accountLogin, false)
          this.refresh()
        }
      },
      immediate: true
    }
  },
  computed: {
    ...mapGetters('symbol', ['getTicker']),
    account: {
      get() {
        return this.value
      },
      set(value) {
        this.$emit('input', value)
      }
    },

  },
  created() {
   // this.intervalId = setInterval(this.refresh, 10 * 1000)
    this.subscribeToTickers(true)
    this.$root.$on("system::tickers::updated", this.refreshTickers)
    this.$root.$on("system::trade::update", this.onTradeUpdated)

  },
  beforeDestroy() {
    //clearInterval(this.intervalId)
    this.subscribeToTickers(false)
    this.$root.$off("system::tickers::updated", this.refreshTickers)
    this.$root.$off("system::trade::update", this.onTradeUpdated)
  },
  methods: {
    ...mapActions('notification', ['subscribeToTickers', 'subscribeToAccount', 'subscribeToTrades']),
    ...mapActions('account', ['getAccountDataByLogin']),
    ...mapActions('trade', ['closeOrder', 'getHistory', 'getHistorySummary', 'deleteOrder']),
    async refresh() {
      try {
        if(this.account == null){
          return;
        }
        this.accountLogin = this.account.id
        const { symbols, account, openTrades, tickers } =  await this.getAccountDataByLogin(this.account.id)
        this.subscribeToWs( this.accountLogin, true)
        this.symbols = symbols
        this.openTrades = openTrades || []
        this.tickers = toObject((tickers || []), 'symbolId')
        this.recalculateProfit();
        // Split view shows open + closed side by side, so load closed too.
        this.refreshHistory();
      } catch (e) {
        this.error = e
        this.showServerErrorNotification('fetchDataError', 'account', e)
      } finally {
        this.loading.page = false
      }
    },
    onHistoryPageClick(event, page) {
      this.history.pagination.page = page;
      // Totals don't change on a page flip — skip the summary re-fetch.
      this.refreshHistory(true);
    },
    refreshHistory(skipSummary) {
      let $this = this;
      this.getHistory({account:this.accountLogin, pagination:$this.history.pagination, sort:$this.history.sort})
          .then((res) => {
            $this.history.data = res.data
            $this.history.pagination.page = res.pagination.page
            $this.history.pagination.total = res.pagination.total
            $this.history.pagination.size = res.pagination.size
          })
          .catch((e) => {
            this.showServerErrorNotification('fetchDataError', 'account', e)
          })
      if (skipSummary !== true) {
        this.getHistorySummary({ account: this.accountLogin })
            .then((summary) => { $this.history.summary = summary })
            .catch(() => { $this.history.summary = null }) // KPI strip falls back to page totals
      }
    },
    subscribeToWs(accountLogin, toggle){
      if(accountLogin == null || accountLogin === 'none'){
        return;
      }
      this.subscribeToAccount({accountId: accountLogin, toggle: toggle})
      this.subscribeToTrades({accountId: accountLogin, toggle: toggle})
    },
    refreshTickers() {
      let $this = this
      let tickers = Object.keys(this.tickers)
      tickers.forEach((ticker) => {
        $this.tickers[ticker] = $this.getTicker(ticker) || $this.tickers[ticker]
      })
      this.recalculateProfit();
    },
    recalculateProfit(){
      let profit = recalculateAllProfits(this.account, this.openTrades, this.symbols, this.tickers)
      let marginFreePercentage = this.calculateMarginFreePercentage();
      console.log('recalculateProfit', profit, marginFreePercentage)
      this.$emit('input',{ ...this.account, profit,marginFreePercentage: marginFreePercentage})
    },
    calculateMarginFreePercentage(){
      if(this.account == null){
        return 0;
      }
      let balance = this.account.balance || 0;
      let credit = this.account.credit || 0;
      this.account.equity = balance + credit;
      this.account.equity += this.openTrades.reduce((acc, trade) => acc + (trade.profit || 0), 0);
      let margin = this.account.margin || 0;
      // let marginFree = equity - margin;
      if(this.account.equity === 0){
        return 0;
      }
      if(margin === 0){
        margin = 1;
      }
      return ((this.account.equity / margin) * 100).toFixed(2);
    },
    onTradeUpdated(trade){
      console.log('onTradeUpdated', trade)
      let index = this.openTrades.findIndex((t) => t.order === trade.order)
      if (index < 0) {
        return
      }
      if(trade.state !== 'OPEN'){
        this.openTrades.splice(index, 1)
      }else {
        this.openTrades[index] = trade
      }
      this.recalculateProfit()
    },
    newTradeRequest() {
      this.$refs.tradeOrderModal.showModal(this.account)
    },
    editTradeRequest(trade) {
      this.$refs.tradeOrderModal.showModal(this.account, trade)
    },
    closeTradeRequest(data) {
      let $this = this
      this.closeOrder({
        account: this.accountLogin,
        orderId: data.trade.order,
        order: {}
      })
          .then(() => {
            $this.openTrades = $this.openTrades.filter((t) => t.order !== data.trade.order)
            $this.refresh()
          }, error => $this.showServerErrorNotification('placeOrderError',null, error))
          .finally(() => {
            data.callback()
          })
    },
    onOrderModalClose(){
      console.log('onOrderModalClose')
      //scroll to top all class .trade-card
      setTimeout(() => {
        document.getElementsByClassName("trade-details-expand").forEach((el) => {
          el.scrollTop = 0;
        })
      }, 1000)
    },
    // Bulk delete (open trades tab): the modal runs the SAME deleteOrder per
    // trade; afterwards clear the tab's selection and refresh the lists.
    bulkDeleteRequest({ trades, callback }) {
      this.bulkDeleteCallback = callback || null
      this.$refs.bulkDeleteModal.open(trades, this.accountLogin)
    },
    onBulkDeleteDone() {
      if (this.bulkDeleteCallback) {
        this.bulkDeleteCallback()
        this.bulkDeleteCallback = null
      }
      this.refresh()
    },
    deleteTradeRequest(form) {
      let $this = this
      let isHistoryTab = this.activeTab === 1;
      this.deleteOrder({
        account: this.accountLogin,
        orderId: form.trade.order,
      })
          .then(() => {
            if(isHistoryTab) {
              $this.history.data = $this.history.data.filter((t) => t.order !== form.trade.order);
              $this.refreshHistory();
            } else {
              this.refresh()
            }

          }, error => this.showServerErrorNotification('placeOrderError', null, error))
          .finally(() => {
            form.callback()
          })
    }
  }
}
</script>

<style lang="scss">
/* Segmented view switch: collapse to Open only, Closed only, or show both */
.tad-seg {
  display: inline-flex;
  gap: 3px;
  padding: 3px;
  margin-bottom: 0.9rem;
  border-radius: 10px;
  background: rgba(110, 107, 130, 0.1);
}
.dark-layout .tad-seg { background: rgba(255, 255, 255, 0.06); }
.tad-seg__btn {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  border: none;
  background: transparent;
  color: #6e6b7b;
  font-weight: 700;
  font-size: 0.82rem;
  line-height: 1.1;
  padding: 0.4rem 0.85rem;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease, box-shadow 0.15s ease;
}
.tad-seg__btn i { font-size: 0.85rem; }
.tad-seg__btn:hover { color: #5e5873; }
.dark-layout .tad-seg__btn { color: #b4b7bd; }
.dark-layout .tad-seg__btn:hover { color: #fff; }
.tad-seg__btn.is-active {
  background: #fff;
  box-shadow: 0 1px 4px rgba(34, 41, 47, 0.15);
}
.dark-layout .tad-seg__btn.is-active { background: #283046; }
.tad-seg__btn--open.is-active { color: #28c76f; }
.tad-seg__btn--closed.is-active { color: #7367f0; }
.tad-seg__btn--both.is-active { color: #5e5873; }
.dark-layout .tad-seg__btn--both.is-active { color: #fff; }

/* Open | Closed side-by-side split (stacks on narrow screens) */
.tad-split {
  display: flex;
  align-items: stretch;   /* so the divider between the columns is full height */
}
/* Single-column mode: the visible side takes the full width, no divider */
.tad-split--single .tad-col:first-child { padding-right: 0; }
.tad-split--single .tad-col:last-child {
  padding-left: 0;
  border-left: none;
}
.tad-col {
  flex: 1 1 0;
  min-width: 0;
}
/* Divider line between Open and Closed so they're never confused */
.tad-col:first-child { padding-right: 1.3rem; }
.tad-col:last-child {
  padding-left: 1.3rem;
  border-left: 2px solid rgba(110, 107, 130, 0.22);
}
.dark-layout .tad-col:last-child { border-left-color: rgba(255, 255, 255, 0.1); }
.tad-col__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-weight: 800;
  font-size: 0.9rem;
  padding: 0.15rem 0.2rem 0.5rem;
  margin-bottom: 0.6rem;
  border-bottom: 2px solid transparent;
}
.tad-col__head--open {
  color: #28c76f;
  border-bottom-color: rgba(40, 199, 111, 0.3);
}
.tad-col__head--closed {
  color: #7367f0;
  border-bottom-color: rgba(115, 103, 240, 0.3);
}
.tad-col__count {
  font-size: 0.72rem;
  font-weight: 800;
  color: #6e6b7b;
  background: rgba(115, 103, 240, 0.1);
  border-radius: 999px;
  padding: 0.1rem 0.55rem;
}
.tad-col__head--open .tad-col__count {
  color: #28c76f;
  background: rgba(40, 199, 111, 0.12);
}

@media (max-width: 991px) {
  .tad-split { flex-direction: column; }
  .tad-col { width: 100%; }
  .tad-col:first-child { padding-right: 0; padding-bottom: 1rem; }
  .tad-col:last-child {
    padding-left: 0;
    padding-top: 1rem;
    border-left: none;
    border-top: 2px solid rgba(110, 107, 130, 0.22);
  }
  .dark-layout .tad-col:last-child { border-left-color: transparent; border-top-color: rgba(255, 255, 255, 0.1); }
}
</style>
